import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { ObjectManager } from './ObjectManager'
import { ResizableSection } from './ResizableSection'
import { ToolPalette } from './ToolPalette'
import { Canvas3D } from './Canvas3D'
import { STLObjectManager } from './STLObjectManager'
import { LoadingStatus, DownloadProgress } from './LoadingStatus'
import { exportSTLString, STLLoadResult, stlLoader } from '../../utils/stlLoader'
import { backendService, Landmark, type InsoleGeneratePayload } from '../../services/backendService'
import { getCachedSTL, cacheSTL } from '../../services/stlCache'
import { useTabContext } from '../../contexts/TabContext'
import { useAuth } from '../../contexts/AuthContext'
import { logInfo, logError } from '../../utils/errorLogger'
import { debugLog, debugError } from '../../utils/debugLogger'
import { APP_VERSION } from '../../version'
import { windowCommandBus, type WindowCommandResult } from '../../utils/windowCommandBus'
import { savePointSet, loadPointSet, listPointSets, deletePointSet } from '../../utils/pointSetPersistence'
import { TemplateUploadDialog, type AlignResult } from './TemplateUploadDialog'
import { TemplateVisualizerDialog, type TemplateOverlay } from './TemplateVisualizerDialog'
import { AdvancedOptionsDialog, loadAdvancedRefine } from './AdvancedOptionsDialog'
import { CADWorkbench } from '../CADWorkbench'
import './GirasolesView.css'

// TEMPORARY FIX: Hardcoded scale factor for measurement tool
// The STL files are already normalized to ~8 units max dimension when stored in the backend.
// For a typical foot (~250mm), this gives a scale factor of 8/250 = 0.032
// TODO: Store original dimensions in backend database and retrieve via API
// See: https://github.com/lacssa/girasoles/issues/XXX
const HARDCODED_SCALE_FACTOR = 0.032

// Constants for left panel sizing
const DEFAULT_LEFT_PANEL_WIDTH = 180 // Increased from 120px
const MIN_LEFT_PANEL_WIDTH = 120
const MAX_LEFT_PANEL_WIDTH = 400
const LEFT_PANEL_STORAGE_KEY = 'girasoles-left-panel-width'

interface SceneObject {
  id: string
  name: string
  type: 'mesh' | 'points' | 'template' | 'grid' | 'annotation'
  visible: boolean
  selectable: boolean
}

interface SelectedPoint {
  id: string
  position: { x: number; y: number; z: number }
  timestamp: number
  type?: 'point' | 'measure' // Type to distinguish between point tool and measure tool
}

interface GirasolesViewWindowProps {
  stlData?: STLLoadResult
  projectId?: string
  tabId?: string
  tabTitle?: string
}

export const GirasolesViewWindow: React.FC<GirasolesViewWindowProps> = ({
  stlData: initialStlData,
  projectId,
  tabId,
  tabTitle
}) => {
  // Test debug logging on component mount
  debugLog('GirasolesViewWindow component mounted/rendered')
  debugLog('projectId:', projectId)
  debugLog('initialStlData:', initialStlData)

  const tabContext = useTabContext()

  const [sceneObjects, setSceneObjects] = useState<SceneObject[]>([])
  const [selectedTool, setSelectedTool] = useState<string | null>('select')
  const [selectedPoints, setSelectedPoints] = useState<SelectedPoint[]>([])
  const [measurePoints, setMeasurePoints] = useState<SelectedPoint[]>([]) // Separate state for measure tool
  const [measureDistance, setMeasureDistance] = useState<number | null>(null) // Distance in mm
  const [selectedObjectId, setSelectedObjectId] = useState<string | null>(null)
  const [showObjectManager, setShowObjectManager] = useState(false)
  const [currentStlData, setCurrentStlData] = useState<STLLoadResult | undefined>(initialStlData)

  // Export STL modal state
  const [showExportModal, setShowExportModal] = useState(false)
  const [tokenBalance, setTokenBalance] = useState<number | null>(null)
  const [isExporting, setIsExporting] = useState(false)

  // Insole generation state
  const [isGeneratingInsole, setIsGeneratingInsole] = useState(false)
  const [showInsoleParamsModal, setShowInsoleParamsModal] = useState(false)
  const [thicknessMm, setThicknessMm] = useState(2)
  const [fadeLengthMm, setFadeLengthMm] = useState(20)
  const [splinePointCount, setSplinePointCount] = useState(2000)
  const [invertFrontBack, setInvertFrontBack] = useState(false)
  const [invertTopBottom, setInvertTopBottom] = useState(false)
  const [footRotationDeg, setFootRotationDeg] = useState(0)
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false)

  // Landmark visualization state
  const [currentStlFileId, setCurrentStlFileId] = useState<string | null>(null)

  // Calibration landmarks state
  const [calibrationLandmarks, _setCalibrationLandmarks] = useState<Landmark[]>([])
  const [originalCalibrationLandmarks, setOriginalCalibrationLandmarks] = useState<Landmark[]>([])
  const [showCalibrationLandmarks, setShowCalibrationLandmarks] = useState(false)
  const [isEditingCalibrationLandmarks, setIsEditingCalibrationLandmarks] = useState(false)
  const [pickingCalibrationPoint, setPickingCalibrationPoint] = useState<string | null>(null)

  // Menu visibility state
  const [showToolsMenu, setShowToolsMenu] = useState(false)

  // Point unselect mode
  const [unselectMode, setUnselectMode] = useState(false)

  // V2 Pipeline modal visibility state
  const [showV2TemplateModal, setShowV2TemplateModal] = useState(false)
  const [_isRunningV2Pipeline, setIsRunningV2Pipeline] = useState(false)

  // Cancellation tracking for in-flight V1/V2 generation. When the user
  // clicks "Cancel" in the loading modal we flip the ref so polling loops
  // exit at the next tick, and we POST to the backend cancel endpoint so
  // server-side stages bail out cooperatively.
  const cancelRequestedRef = useRef(false)
  const activeV2SessionIdRef = useRef<string | null>(null)
  const activeV1ProjectIdRef = useRef<string | null>(null)
  const [isCancelling, setIsCancelling] = useState(false)
  // V2 full editor (CADWorkbench) overlay
  const [showCADWorkbench, setShowCADWorkbench] = useState(false)

  // Test support — expose CADWorkbench trigger for Playwright E2E tests (issue #156)
  useEffect(() => {
    (window as any).__openCADWorkbench = () => {
      if (projectId && currentStlFileId) setShowCADWorkbench(true)
    }
    return () => { delete (window as any).__openCADWorkbench }
  }, [projectId, currentStlFileId])

  // Upload Template modal visibility state (kept for CIW upload-template
  // command back-compat; the toolbar entry was removed).
  const [showUploadTemplateModal, setShowUploadTemplateModal] = useState(false)

  // Template visualizer state
  const [showTemplateVisualizer, setShowTemplateVisualizer] = useState(false)
  const [templateOverlay, setTemplateOverlay] = useState<TemplateOverlay | null>(null)

  // V2 align gizmo mode (independent from the Tool Palette so the user
  // can rotate / scale the template overlay without losing orbit-camera
  // controls). Drives Canvas3D's TransformControls when the overlay is
  // visible.
  const [v2GizmoMode, setV2GizmoMode] = useState<'translate' | 'rotate' | 'scale'>('translate')

  // Bottom-right Navigator panel: current camera view label + numeric
  // azim/elev/dist read out of OrbitControls so the user can tell the
  // assistant exactly which view they are looking at.
  const [viewInfo, setViewInfo] = useState<{ azim: number; elev: number; dist: number; label: string }>({
    azim: 45,
    elev: 35,
    dist: 8.66,
    label: 'ISO',
  })

  // Advanced Insole Options dialog (Tools menu)
  const [showAdvancedInsoleOptions, setShowAdvancedInsoleOptions] = useState(false)

  // Foot measurements (calibrated dimensions of currently loaded STL)
  const [footMeasurements, setFootMeasurements] = useState<{
    foot_length?: number
    foot_width?: number
    arch_height?: number
    arch_classification?: string
    heel_width?: number
    instep_height?: number
    units?: string
  } | null>(null)
  const [footMeasurementsLoading, setFootMeasurementsLoading] = useState(false)

  // Insole template selection (lifted from V2 dialog so it can be surfaced
  // in the G-Object Manager + Insole Characteristics card).
  const [insoleSelection, setInsoleSelection] = useState<{
    templateId: string
    templateName: string
    size: string
    gender: string
    type: string
  } | null>(null)

  // Ref to the canvas-area DOM node so the V2 alignment side-panel can be
  // portalled inside it (pinned to the right edge of the 3D canvas instead
  // of the full viewport, which would overlap the global Admin Panel button).
  const canvasAreaRef = useRef<HTMLDivElement>(null)
  const [canvasAreaEl, setCanvasAreaEl] = useState<HTMLDivElement | null>(null)
  useEffect(() => {
    setCanvasAreaEl(canvasAreaRef.current)
  }, [])

  // Listen for camera-view changes published by Canvas3D so the bottom-right
  // Navigator panel can show the user's current view.
  useEffect(() => {
    const onChanged = (e: Event) => {
      const d = (e as CustomEvent).detail as { azim: number; elev: number; dist: number; label: string }
      if (d) setViewInfo(d)
    }
    window.addEventListener('gv:view-changed', onChanged as EventListener)
    return () => window.removeEventListener('gv:view-changed', onChanged as EventListener)
  }, [])

  const { isAuthenticated } = useAuth()

  // Loading state management
  const [isLoadingSTL, setIsLoadingSTL] = useState(false)
  const [loadingMessage, setLoadingMessage] = useState('')
  const [loadingDetails, setLoadingDetails] = useState<string[]>([])
  const [loadingStartedAt, setLoadingStartedAt] = useState<number | null>(null)
  const [loadingElapsedSec, setLoadingElapsedSec] = useState(0)
  const [loadingProgress, setLoadingProgress] = useState<number | undefined>(undefined)
  const [downloadProgress] = useState<DownloadProgress | undefined>(undefined) // Reserved for future chunked download support
  const [loadingError, setLoadingError] = useState<string | null>(null)

  // Left panel resize state
  const [leftPanelWidth, setLeftPanelWidth] = useState<number>(() => {
    const saved = localStorage.getItem(LEFT_PANEL_STORAGE_KEY)
    return saved ? parseInt(saved, 10) : DEFAULT_LEFT_PANEL_WIDTH
  })
  const [isResizingPanel, setIsResizingPanel] = useState(false)
  const resizeStartX = useRef<number>(0)
  const resizeStartWidth = useRef<number>(0)

  // Left panel resize handlers
  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizingPanel(true)
    resizeStartX.current = e.clientX
    resizeStartWidth.current = leftPanelWidth
  }

  useEffect(() => {
    if (!isResizingPanel) return

    document.body.classList.add('resizing-panel')

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - resizeStartX.current
      // Panel is anchored on the LEFT of the canvas; dragging the handle
      // RIGHT (deltaX > 0) grows the panel.
      let newWidth = resizeStartWidth.current + deltaX

      // Clamp width between min and max
      newWidth = Math.max(MIN_LEFT_PANEL_WIDTH, Math.min(MAX_LEFT_PANEL_WIDTH, newWidth))

      setLeftPanelWidth(newWidth)
    }

    const handleMouseUp = () => {
      setIsResizingPanel(false)
      document.body.classList.remove('resizing-panel')
      // Save width to localStorage
      localStorage.setItem(LEFT_PANEL_STORAGE_KEY, leftPanelWidth.toString())
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.classList.remove('resizing-panel')
    }
  }, [isResizingPanel, leftPanelWidth])

  // Refs for command handler to avoid stale closures
  const selectedPointsRef = useRef(selectedPoints)
  selectedPointsRef.current = selectedPoints
  const projectIdRef = useRef(projectId)
  projectIdRef.current = projectId
  const generateInsoleRef = useRef<() => void>(() => {})

  // Command handler for WindowCommandBus
  const handleBusCommand = useCallback(
    async (command: string, args?: Record<string, unknown>): Promise<WindowCommandResult> => {
      switch (command) {
        case 'list-points': {
          const pts = selectedPointsRef.current
          return {
            success: true,
            data: pts.map((p, i) => ({
              index: i,
              x: p.position.x,
              y: p.position.y,
              z: p.position.z,
            })),
          }
        }

        case 'clear-points':
          setSelectedPoints([])
          return { success: true, data: 'Points cleared' }

        case 'save-points': {
          const name = (args?.name as string) || 'default'
          const pts = selectedPointsRef.current
          if (pts.length === 0) {
            return { success: false, error: 'No points to save' }
          }
          const entry = savePointSet(name, pts, projectIdRef.current)
          return { success: true, data: `Saved ${entry.pointCount} points as "${name}"` }
        }

        case 'load-points': {
          const name = (args?.name as string) || 'default'
          const pts = loadPointSet(name, projectIdRef.current)
          if (!pts || pts.length === 0) {
            return { success: false, error: `No saved point set "${name}" found` }
          }
          setSelectedPoints(pts as SelectedPoint[])
          return { success: true, data: `Loaded ${pts.length} points from "${name}"` }
        }

        case 'list-saves': {
          const sets = listPointSets(projectIdRef.current)
          return {
            success: true,
            data: sets.map(s => ({
              name: s.name,
              points: s.pointCount,
              updated: s.updatedAt,
            })),
          }
        }

        case 'delete-save': {
          const name = (args?.name as string) || 'default'
          const ok = deletePointSet(name, projectIdRef.current)
          return ok
            ? { success: true, data: `Deleted point set "${name}"` }
            : { success: false, error: `Point set "${name}" not found` }
        }

        case 'get-state': {
          return {
            success: true,
            data: {
              projectId: projectIdRef.current,
              pointCount: selectedPointsRef.current.length,
              selectedTool,
            },
          }
        }

        case 'generate-insole':
          generateInsoleRef.current()
          return { success: true, data: 'Insole generation dialog opened' }

        case 'upload-template':
          setShowUploadTemplateModal(true)
          return { success: true, data: 'Upload template dialog opened' }

        case 'list-templates': {
          try {
            const resp = await backendService.listTemplates(1, 100)
            return {
              success: true,
              data: resp.templates.map(t => ({
                id: t.id,
                name: t.name,
                size: t.size,
                type: t.type,
                is_shared: t.is_shared,
                created_by: t.created_by,
              })),
            }
          } catch (err) {
            return { success: false, error: String(err) }
          }
        }

        case 'delete-template': {
          const id = args?.id as string
          if (!id) return { success: false, error: 'Usage: gv delete-template <id>' }
          try {
            await backendService.deleteTemplate(id)
            return { success: true, data: `Template ${id} deleted` }
          } catch (err) {
            return { success: false, error: String(err) }
          }
        }

        default:
          return { success: false, error: `Unknown command: ${command}` }
      }
    },
    [selectedTool]
  )

  // Register with WindowCommandBus
  useEffect(() => {
    if (!tabId) return
    const label = tabTitle || 'Girasoles View'
    const unregister = windowCommandBus.register(
      'girasoles_view',
      tabId,
      label,
      handleBusCommand
    )
    return unregister
  }, [tabId, tabTitle, handleBusCommand])

  useEffect(() => {
    if (!isLoadingSTL || !loadingStartedAt) {
      setLoadingElapsedSec(0)
      return
    }

    const timerId = window.setInterval(() => {
      setLoadingElapsedSec(Math.floor((Date.now() - loadingStartedAt) / 1000))
    }, 1000)

    return () => window.clearInterval(timerId)
  }, [isLoadingSTL, loadingStartedAt])

  // Fetch foot measurements whenever the loaded STL fileId changes.
  // The same calibrated measurements shown in STL_IN are surfaced here in
  // the G-Object Manager column so the user does not have to switch tabs.
  useEffect(() => {
    let cancelled = false
    if (!currentStlFileId) {
      setFootMeasurements(null)
      setFootMeasurementsLoading(false)
      return () => {
        cancelled = true
      }
    }
    setFootMeasurementsLoading(true)
    backendService
      .getMeasurements(currentStlFileId)
      .then((res) => {
        if (cancelled) return
        if (res.calibrated && res.measurements) {
          setFootMeasurements(res.measurements)
        } else {
          setFootMeasurements(null)
        }
      })
      .catch((err) => {
        if (cancelled) return
        debugError('Failed to fetch foot measurements:', err)
        setFootMeasurements(null)
      })
      .finally(() => {
        if (!cancelled) setFootMeasurementsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [currentStlFileId])

  const handleObjectVisibilityToggle = (objectId: string) => {
    setSceneObjects(prev =>
      prev.map(obj =>
        obj.id === objectId ? { ...obj, visible: !obj.visible } : obj
      )
    )
  }

  const handleObjectSelect = (objectId: string) => {
    console.log('Toggle select object:', objectId)
    // Toggle selection: if already selected, deselect; otherwise select
    setSelectedObjectId(prev => prev === objectId ? null : objectId)
  }

  const handleSceneObjectsChange = (objects: SceneObject[]) => {
    console.log('Scene objects updated:', objects)
    setSceneObjects(prev => {
      // Merge: ensure 'stl-mesh' is never dropped when STL data is loaded
      const hasStlMesh = objects.some(o => o.id === 'stl-mesh')
      if (!hasStlMesh && currentStlData) {
        const existing = prev.find(o => o.id === 'stl-mesh')
        if (existing) {
          return [existing, ...objects.filter(o => o.id !== 'stl-mesh')]
        }
        return [
          { id: 'stl-mesh', name: 'STL Mesh', type: 'mesh' as const, visible: true, selectable: true },
          ...objects
        ]
      }
      return objects
    })
  }

  const handleObjectDelete = (objectId: string) => {
    if (objectId === 'v2-insole-cut') {
      setTemplateOverlay(null)
      setInsoleSelection(null)
      return
    }
    setSceneObjects(prev => prev.filter(obj => obj.id !== objectId))
  }

  // Compose the list shown in G-Object Manager. We synthesise a dedicated
  // "Insole Template Cut" row whenever a V2 alignment overlay is active,
  // so the user can toggle its visibility / clear it from the same panel
  // they use for the foot mesh.
  const displayedSceneObjects = useMemo<SceneObject[]>(() => {
    const base = sceneObjects.slice()
    if (templateOverlay && templateOverlay.id === 'v2-align-preview') {
      base.push({
        id: 'v2-insole-cut',
        name: insoleSelection?.templateName || templateOverlay.name || 'Insole Template Cut',
        type: 'template',
        visible: templateOverlay.visible !== false,
        selectable: true,
      })
    }
    return base
  }, [sceneObjects, templateOverlay, insoleSelection])

  const handleDisplayedVisibilityToggle = (objectId: string) => {
    if (objectId === 'v2-insole-cut') {
      setTemplateOverlay(prev => prev ? { ...prev, visible: !prev.visible } : prev)
      return
    }
    handleObjectVisibilityToggle(objectId)
  }

  const handleToolSelect = (toolId: string) => {
    // Handle export-stl tool separately
    if (toolId === 'export-stl') {
      handleExportSTL()
      return
    }

    // Handle generate-insole (Classic method)
    if (toolId === 'generate-insole') {
      handleGenerateInsole()
      return
    }

    // Handle V2 Pipeline
    if (toolId === 'v2-pipeline') {
      if (projectId && currentStlFileId) {
        // Inline V2 flow: TemplateUploadDialog hosts the full
        // template-select → align-preview (2D plantar projection +
        // green template overlay with manual translate/rotate/scale) →
        // generate sequence inside the GirasolesView window. This keeps
        // the alignment UX on the Girasoles canvas instead of jumping
        // into a separate CADWorkbench tab.
        setShowV2TemplateModal(true)
        logInfo('Opening V2 template selection (inline align + generate)', 'GirasolesView')
      } else {
        alert('Please load an STL file first.')
      }
      return
    }

    // Handle V2 Pipeline (full CADWorkbench overlay — kept for power users)
    if (toolId === 'v2-pipeline-cad') {
      if (projectId && currentStlFileId) {
        setShowCADWorkbench(true)
        logInfo('Opening V2 CAD Workbench (full editor)', 'GirasolesView')
      } else {
        alert('Please load an STL file first.')
      }
      return
    }

    // Handle V2 Pipeline (legacy quick-generate dialog, kept for fast path)
    if (toolId === 'v2-pipeline-quick') {
      if (projectId && currentStlFileId) {
        setShowV2TemplateModal(true)
        logInfo('Opening V2 template selection (quick)', 'GirasolesView')
      } else {
        alert('Please load an STL file first.')
      }
      return
    }

    // Clear measure points when switching away from measure tool
    if (selectedTool === 'measure' && toolId !== 'measure') {
      setMeasurePoints([])
      setMeasureDistance(null)
    }
    setSelectedTool(toolId)
    console.log('Selected tool:', toolId)
  }

  const handleExportSTL = async () => {
    // Open export confirmation modal
    setShowExportModal(true)
    // Fetch token balance
    try {
      const balance = await backendService.getTokenBalance()
      setTokenBalance(balance.balance)
    } catch (error) {
      console.error('Failed to fetch token balance:', error)
      setTokenBalance(null)
    }
  }

  const handleConfirmExport = async () => {
    if (!projectId) {
      alert('No project ID available for export')
      return
    }

    if (!currentStlData?.geometry) {
      alert('No STL geometry loaded. Please load a project before exporting.')
      return
    }

    setIsExporting(true)
    try {
      const authToken = backendService.getAuthToken()
      const stlString = exportSTLString(currentStlData.geometry, currentStlData.scaleFactor)
      const exportFileName = `project-${projectId}-stl-out.stl`
      const exportFile = new File([stlString], exportFileName, { type: 'model/stl' })

      await backendService.uploadSTLFile(exportFile, projectId, undefined, undefined, undefined, 'stl_out')

      const downloadUrl = backendService.getSTLDownloadUrl(projectId, 'stl_out')
      const response = await fetch(downloadUrl, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
        credentials: 'include',
      })

      if (response.status === 402) {
        throw new Error('Insufficient tokens. Please add more tokens to continue.')
      }

      if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`)
      }

      const blob = await response.blob()
      const blobUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = exportFileName

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(blobUrl)

      alert('STL exported successfully!')
      setShowExportModal(false)
    } catch (error: any) {
      if (error.message.includes('Insufficient tokens')) {
        alert(error.message)
      } else {
        alert(`Export failed: ${error.message}`)
      }
    } finally {
      setIsExporting(false)
    }
  }

  const handleToggleCalibrationLandmarks = async () => {
    debugLog(`Toggle calibration landmarks. Current state: ${showCalibrationLandmarks}`)

    if (showCalibrationLandmarks) {
      // Hide calibration landmarks
      setShowCalibrationLandmarks(false)
      _setCalibrationLandmarks([])
      logInfo('Calibration landmarks hidden', 'GirasolesView')
    } else {
      // Show calibration landmarks - fetch from API
      if (!currentStlFileId) {
        const msg = 'No STL file loaded. Please load an STL file first.'
        logError(msg, 'GirasolesView')
        debugLog(msg)
        alert(msg)
        return
      }

      try {
        debugLog(`Fetching calibration for STL file: ${currentStlFileId}`)
        logInfo(`Fetching calibration for STL file: ${currentStlFileId}`, 'GirasolesView')
        const response = await backendService.getCalibration(currentStlFileId)
        debugLog(`Calibration response:`, response)

        // Check for calibration data - backend returns snake_case (calibration_points)
        const calibrationPoints = (response as any).calibration_points || response.calibrationPoints

        if (!response.calibrated || !calibrationPoints) {
          const msg = 'No calibration found for this STL file. Please calibrate the foot first.'
          logInfo(msg, 'GirasolesView')
          debugLog(msg)
          alert(msg)
          setShowCalibrationLandmarks(false)
          return
        }

        // Convert calibration points from real-world mm to scene space.
        // The STL is normalized (centered + scaled to fit 8 units) via
        // stlLoader.normalizeGeometry(). scaleFactor = sceneUnits / realUnits.
        const sf = currentStlData?.scaleFactor ?? HARDCODED_SCALE_FACTOR
        const toScene = (pt: { x: number; y: number; z: number }) => ({
          x: pt.x * sf,
          y: pt.y * sf,
          z: pt.z * sf,
        })

        const calibrationLandmarksData: Landmark[] = [
          {
            id: 'heel',
            stl_file_id: currentStlFileId,
            landmark_type: 'Heel',
            coordinates_3d: toScene(calibrationPoints.heel),
            confidence: 1.0,
            detection_method: 'manual'
          },
          {
            id: 'mt1',
            stl_file_id: currentStlFileId,
            landmark_type: 'MT1',
            coordinates_3d: toScene(calibrationPoints.mt1),
            confidence: 1.0,
            detection_method: 'manual'
          },
          {
            id: 'mt5',
            stl_file_id: currentStlFileId,
            landmark_type: 'MT5',
            coordinates_3d: toScene(calibrationPoints.mt5),
            confidence: 1.0,
            detection_method: 'manual'
          }
        ]

        _setCalibrationLandmarks(calibrationLandmarksData)
        setOriginalCalibrationLandmarks(JSON.parse(JSON.stringify(calibrationLandmarksData)))
        setShowCalibrationLandmarks(true)
            logInfo(`Showing ${calibrationLandmarksData.length} calibration landmarks`, 'GirasolesView')
        debugLog(`Calibration landmarks:`, calibrationLandmarksData)
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to load calibration'
        logError(`Failed to load calibration: ${errorMessage}`, 'GirasolesView')
        debugLog(`Error loading calibration:`, error)
        alert(`Failed to load calibration: ${errorMessage}`)
        setShowCalibrationLandmarks(false)
      }
    }
  }

  // --- Calibration landmark edit actions ---

  const handleAcceptCalibration = async () => {
    if (!currentStlFileId || calibrationLandmarks.length === 0) return
    const h = calibrationLandmarks.find(l => l.id === 'heel')?.coordinates_3d
    const m1 = calibrationLandmarks.find(l => l.id === 'mt1')?.coordinates_3d
    const m5 = calibrationLandmarks.find(l => l.id === 'mt5')?.coordinates_3d
    if (!h || !m1 || !m5) return
    try {
      // Convert from scene space back to real-world mm for backend storage
      const sf = currentStlData?.scaleFactor ?? HARDCODED_SCALE_FACTOR
      const invSf = sf !== 0 ? 1 / sf : 1
      await backendService.saveCalibration(currentStlFileId, {
        heel: { x: h.x * invSf, y: h.y * invSf, z: h.z * invSf },
        mt1:  { x: m1.x * invSf, y: m1.y * invSf, z: m1.z * invSf },
        mt5:  { x: m5.x * invSf, y: m5.y * invSf, z: m5.z * invSf },
      })
      setOriginalCalibrationLandmarks(JSON.parse(JSON.stringify(calibrationLandmarks)))
      logInfo('Calibration saved', 'GirasolesView')
    } catch (err) {
      logError(`Failed to save calibration: ${err}`, 'GirasolesView')
      alert('Failed to save calibration.')
    }
  }

  const handleCancelCalibration = () => {
    if (originalCalibrationLandmarks.length > 0) {
      _setCalibrationLandmarks(JSON.parse(JSON.stringify(originalCalibrationLandmarks)))
    }
    setIsEditingCalibrationLandmarks(false)
    logInfo('Calibration edit cancelled', 'GirasolesView')
  }

  const handleResetCalibration = () => {
    if (originalCalibrationLandmarks.length > 0) {
      _setCalibrationLandmarks(JSON.parse(JSON.stringify(originalCalibrationLandmarks)))
      logInfo('Calibration landmarks reset to original positions', 'GirasolesView')
    }
  }

  const handleCalibrationPointPicked = (landmarkId: string, position: { x: number; y: number; z: number }) => {
    // Update the individual landmark with the new surface position
    _setCalibrationLandmarks(prev => {
      const updated = prev.map(l =>
        l.id === landmarkId
          ? { ...l, coordinates_3d: { x: position.x, y: position.y, z: position.z } }
          : l
      )
      // Also persist to backend in real-world mm
      if (currentStlFileId) {
        const h = updated.find(l => l.id === 'heel')?.coordinates_3d
        const m1 = updated.find(l => l.id === 'mt1')?.coordinates_3d
        const m5 = updated.find(l => l.id === 'mt5')?.coordinates_3d
        if (h && m1 && m5) {
          const sf = currentStlData?.scaleFactor ?? HARDCODED_SCALE_FACTOR
          const invSf = sf !== 0 ? 1 / sf : 1
          backendService.saveCalibration(currentStlFileId, {
            heel: { x: h.x * invSf, y: h.y * invSf, z: h.z * invSf },
            mt1:  { x: m1.x * invSf, y: m1.y * invSf, z: m1.z * invSf },
            mt5:  { x: m5.x * invSf, y: m5.y * invSf, z: m5.z * invSf },
          }).catch(err => logError(`Failed to save calibration: ${err}`, 'GirasolesView'))
        }
      }
      return updated
    })
    // Clear picking mode after successful placement
    setPickingCalibrationPoint(null)
    logInfo(`Calibration point '${landmarkId}' re-placed at surface`, 'GirasolesView')
  }

  const handlePointSelect = (point: { x: number; y: number; z: number }) => {
    if (selectedTool === 'measure') {
      // Measure tool: only allow 2 points
      const newPoint: SelectedPoint = {
        id: `measure_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        position: point,
        timestamp: Date.now(),
        type: 'measure'
      }

      setMeasurePoints(prev => {
        const updated = [...prev, newPoint]

        // If we have 2 points, calculate distance
        if (updated.length === 2) {
          const p1 = updated[0].position
          const p2 = updated[1].position

          // Calculate distance in scene units
          const distanceInSceneUnits = Math.sqrt(
            Math.pow(p2.x - p1.x, 2) +
            Math.pow(p2.y - p1.y, 2) +
            Math.pow(p2.z - p1.z, 2)
          )

          // Convert to real-world units (mm) using HARDCODED scale factor
          // TEMPORARY FIX: Using hardcoded scale factor since STL files are pre-normalized in backend
          // scaleFactor = sceneUnits / realUnits, so realUnits = sceneUnits / scaleFactor
          const distanceInMM = distanceInSceneUnits / HARDCODED_SCALE_FACTOR

          setMeasureDistance(distanceInMM)
          console.log('Measure distance:', distanceInMM, 'mm (scene units:', distanceInSceneUnits, ', hardcoded scale factor:', HARDCODED_SCALE_FACTOR, ')')
        } else if (updated.length > 2) {
          // Reset to just the new point if more than 2
          setMeasureDistance(null)
          return [newPoint]
        }

        return updated
      })
      console.log('Measure point selected:', newPoint)
    } else if (selectedTool === 'point') {
      if (unselectMode) {
        // In unselect mode, remove the nearest point within threshold
        const threshold = 0.5 // scene units
        let nearestIdx = -1
        let nearestDist = Infinity
        for (let i = 0; i < selectedPoints.length; i++) {
          const p = selectedPoints[i].position
          const dist = Math.sqrt(
            (p.x - point.x) ** 2 + (p.y - point.y) ** 2 + (p.z - point.z) ** 2
          )
          if (dist < nearestDist) {
            nearestDist = dist
            nearestIdx = i
          }
        }
        if (nearestIdx >= 0 && nearestDist < threshold) {
          setSelectedPoints(prev => prev.filter((_, i) => i !== nearestIdx))
          console.log('Point unselected at index', nearestIdx)
        }
      } else {
        // Point tool: allow unlimited points
        const newPoint: SelectedPoint = {
          id: `point_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          position: point,
          timestamp: Date.now(),
          type: 'point'
        }
        setSelectedPoints(prev => [...prev, newPoint])
        console.log('Point selected:', newPoint)
      }
    }
  }

  const handleClearPoints = () => {
    setSelectedPoints([])
    console.log('All points cleared')
  }

  const handleSTLObjectSelect = async (stlObject: any) => {
    debugLog('handleSTLObjectSelect called with:', stlObject)
    debugLog('projectId:', projectId)

    if (!projectId) {
      debugError('No projectId available')
      setLoadingError('No project ID available. Cannot load STL.')
      return
    }

    debugLog('Setting loading state...')
    setIsLoadingSTL(true)
    setLoadingError(null)
    setLoadingProgress(0)

    try {
      // Step 0: Check IndexedDB cache (uses fileId + uploadDate from status)
      const uploadedAt = stlObject.uploadDate ?? stlObject.uploadedAt ?? null
      const cachedBuffer = stlObject.fileId
        ? await getCachedSTL(projectId, 'stl_in', { fileId: stlObject.fileId, uploadedAt })
        : null

      let arrayBuffer: ArrayBuffer

      if (cachedBuffer) {
        logInfo(`⚡ STL cache HIT — skipping download for ${stlObject.originalName}`, 'GirasolesView')
        setLoadingMessage('Loading from browser cache...')
        setLoadingProgress(40)
        arrayBuffer = cachedBuffer
      } else {
      // Step 1: Downloading
      debugLog('Step 1: Starting download')
      setLoadingMessage('Downloading STL file from backend...')
      setLoadingProgress(10)
      logInfo(`Loading STL object from backend: ${stlObject.originalName}`, 'GirasolesView')

      // Download the STL file from backend with authentication
      const downloadUrl = backendService.getSTLDownloadUrl(projectId)
      debugLog('Download URL:', downloadUrl)

      const headers: { [key: string]: string } = {}
      const authToken = backendService.getAuthToken()
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`
        debugLog('Auth token added to headers')
      }

      debugLog('Calling fetch...')
      const infoResponse = await fetch(downloadUrl, { headers, credentials: 'include' })
      debugLog('Fetch completed, status:', infoResponse.status)

      if (!infoResponse.ok) {
        debugError('Response not OK:', infoResponse.status, infoResponse.statusText)
        throw new Error(`HTTP ${infoResponse.status}: ${infoResponse.statusText}`)
      }

      // Backend may return JSON with R2 presigned URL or stream the file directly
      let fileResponse: Response
      const contentType = infoResponse.headers.get('Content-Type') ?? ''
      if (contentType.includes('application/json')) {
        const { url: r2Url } = await infoResponse.json() as { url: string }
        debugLog('Using R2 pre-signed URL for download')
        logInfo('Using R2 pre-signed URL for CDN download', 'GirasolesView')
        fileResponse = await fetch(r2Url)
        if (!fileResponse.ok) {
          throw new Error(`R2 HTTP ${fileResponse.status}: ${fileResponse.statusText}`)
        }
      } else {
        fileResponse = infoResponse
      }

      // Get file size
      const contentLength = fileResponse.headers.get('content-length')
      const fileSizeMB = contentLength ? (parseInt(contentLength) / (1024 * 1024)).toFixed(2) : 'unknown'
      debugLog(`File size: ${fileSizeMB} MB`)

      // Step 2: Processing
      debugLog('Step 2: Converting to arrayBuffer')
      setLoadingMessage('Processing STL data...')
      setLoadingProgress(40)

      const arrayBufferStartTime = Date.now()
      arrayBuffer = await fileResponse.arrayBuffer()
      const arrayBufferDuration = Date.now() - arrayBufferStartTime
      debugLog(`ArrayBuffer received in ${arrayBufferDuration}ms, size: ${(arrayBuffer.byteLength / (1024 * 1024)).toFixed(2)} MB`)

      // Cache the downloaded STL in IndexedDB for next time
      if (stlObject.fileId) {
        cacheSTL(projectId, 'stl_in', {
          fileId: stlObject.fileId,
          uploadedAt,
          filename: stlObject.originalName,
          sizeBytes: arrayBuffer.byteLength,
        }, arrayBuffer).catch(() => {/* best-effort */})
      }

      } // end cache-miss else block

      // Step 3: Loading geometry
      debugLog('Step 3: Parsing STL geometry')
      setLoadingMessage('Loading geometry...')
      setLoadingProgress(60)

      const parseStartTime = Date.now()
      const result = await stlLoader.loadSTLFromArrayBuffer(arrayBuffer)
      const parseDuration = Date.now() - parseStartTime
      debugLog(`STL parsed in ${parseDuration}ms`)

      // Step 4: Normalizing
      debugLog('Step 4: Normalizing geometry')
      setLoadingMessage('Normalizing geometry for viewing...')
      setLoadingProgress(80)

      const scaleFactor = stlLoader.normalizeGeometry(result.geometry, 8)
      result.scaleFactor = scaleFactor
      debugLog(`Normalization completed with scale factor: ${scaleFactor}`)

      // Step 5: Rendering
      debugLog('Step 5: Setting STL data for rendering')
      setLoadingMessage('Rendering mesh...')
      setLoadingProgress(95)

      setCurrentStlData(result)
      setCurrentStlFileId(stlObject.fileId)
      debugLog('STL data set successfully')

      // Complete
      setLoadingProgress(100)
      logInfo(`STL object loaded successfully: ${stlObject.originalName}`, 'GirasolesView')
      debugLog('STL loading completed successfully')

      // Close loading modal after a brief delay
      setTimeout(() => {
        debugLog('Closing loading modal')
        setIsLoadingSTL(false)
        setLoadingProgress(undefined)
      }, 500)

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      debugError('Error during STL loading:', errorMessage)
      logError(`Failed to load STL object: ${errorMessage}`, 'GirasolesView')
      setLoadingError(`Failed to load STL file: ${errorMessage}`)
      setIsLoadingSTL(false)
    }
  }

  // Handle initialStlData that needs scale factor correction
  // This runs when STL is pre-loaded (from Library Manager) without proper scale factor
  useEffect(() => {
    if (initialStlData) {
      const currentScaleFactor = initialStlData.scaleFactor
      debugLog('Checking initialStlData scale factor:', currentScaleFactor)

      // Need to re-normalize if:
      // 1. scaleFactor is undefined (not set)
      // 2. scaleFactor is 1 (incorrect default value)
      if (currentScaleFactor === undefined || currentScaleFactor === 1) {
        debugLog(`Initial STL data has invalid scale factor (${currentScaleFactor}) - re-normalizing...`)
        // Re-normalize to capture correct scale factor
        const newScaleFactor = stlLoader.normalizeGeometry(initialStlData.geometry, 8)
        debugLog(`Re-normalized with scale factor: ${newScaleFactor}`)

        // Update the initialStlData object and trigger state update
        const updatedStlData = { ...initialStlData, scaleFactor: newScaleFactor }
        setCurrentStlData(updatedStlData)
        debugLog('Updated currentStlData with correct scale factor')
      } else {
        debugLog(`Initial STL data has valid scale factor: ${currentScaleFactor}`)
        // If scale factor is already correct, just use it
        setCurrentStlData(initialStlData)
      }

      // Keep STL file id aligned with currently loaded data when available.
      if (initialStlData.fileId) {
        setCurrentStlFileId(initialStlData.fileId)
      }
    }
  }, [initialStlData])

  // Ensure the STL currently being viewed is always represented in Object Manager
  // and auto-select it when first entering from STL Input View.
  useEffect(() => {
    if (!currentStlData) return
    setSceneObjects(prev => {
      const hasStlMesh = prev.some(obj => obj.id === 'stl-mesh')
      if (hasStlMesh) return prev
      return [
        {
          id: 'stl-mesh',
          name: 'STL Mesh',
          type: 'mesh',
          visible: true,
          selectable: true
        },
        ...prev
      ]
    })
    // Auto-select the STL mesh so it's active by default
    setSelectedObjectId(prev => prev ?? 'stl-mesh')
  }, [currentStlData])

  // When GirasolesView is opened directly from STL_IN, we may already have geometry
  // but not the corresponding backend stl_in fileId in this component state.
  // Resolve it from project status so "Generate Insole" can be enabled.
  useEffect(() => {
    const resolveCurrentStlFileId = async () => {
      if (!projectId || currentStlFileId) return

      try {
        const status = await backendService.getStlInStatus(projectId)
        if (status.exists && status.latest?.fileId) {
          setCurrentStlFileId(status.latest.fileId)
        }
      } catch (error) {
        const msg = error instanceof Error ? error.message : 'Failed to resolve current STL file id'
        logError(msg, 'GirasolesView')
      }
    }

    void resolveCurrentStlFileId()
  }, [projectId, currentStlFileId])

  // Auto-load STL geometry when we have a file ID but no geometry data.
  // This happens when GirasolesView is opened from Library Manager with only a projectId.
  const autoLoadAttempted = useRef(false)
  useEffect(() => {
    if (!projectId || !currentStlFileId || currentStlData || autoLoadAttempted.current) return
    autoLoadAttempted.current = true

    const autoLoadStl = async () => {
      try {
        const status = await backendService.getStlInStatus(projectId)
        if (status.exists && status.latest) {
          handleSTLObjectSelect({
            fileId: status.latest.fileId,
            originalName: status.latest.filename,
            uploadDate: status.latest.uploadedAt || 'Unknown',
            metadata: {
              vertices: status.latest.vertices,
              faces: status.latest.faces,
              size: { x: 0, y: 0, z: 0 }
            }
          })
        }
      } catch (error) {
        const msg = error instanceof Error ? error.message : 'Failed to auto-load STL'
        logError(msg, 'GirasolesView')
      }
    }

    void autoLoadStl()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, currentStlFileId, currentStlData])

  const handleGenerateInsole = () => {
    if (!projectId) {
      alert('No project ID available for insole generation.')
      return
    }
    if (selectedPoints.length < 3) {
      alert('Please select at least 3 boundary points.')
      return
    }
    setShowInsoleParamsModal(true)
  }
  generateInsoleRef.current = handleGenerateInsole

  const handleConfirmGenerateInsole = async () => {
    if (!projectId) {
      alert('No project ID available for insole generation.')
      return
    }
    let resolvedStlFileId = currentStlFileId
    if (!resolvedStlFileId) {
      try {
        const status = await backendService.getStlInStatus(projectId)
        resolvedStlFileId = status.exists ? status.latest?.fileId ?? null : null
        if (resolvedStlFileId) {
          setCurrentStlFileId(resolvedStlFileId)
        }
      } catch {
        // If status fetch fails we handle it below with a user-facing alert.
      }
    }
    if (!resolvedStlFileId) {
      alert('STL_IN file id not available yet. Please reload STL_IN and try again.')
      return
    }

    // Fallback to 1 when missing; we still proceed to avoid blocking generation.
    const scaleFactor = currentStlData?.scaleFactor ?? 1
    if (selectedPoints.length < 3) {
      alert('Please select at least 3 boundary points.')
      return
    }
    if (splinePointCount < 1000) {
      alert('Spline point count must be at least 1000.')
      return
    }
    if (thicknessMm <= 0) {
      alert('Thickness must be greater than 0.')
      return
    }
    if (fadeLengthMm <= 0) {
      alert('Fade length must be greater than 0.')
      return
    }
    if (isGeneratingInsole || isLoadingSTL) return

    cancelRequestedRef.current = false
    activeV1ProjectIdRef.current = projectId ?? null
    setIsCancelling(false)
    setIsGeneratingInsole(true)
    setLoadingError(null)
    setIsLoadingSTL(true)
    setLoadingStartedAt(Date.now())
    setLoadingMessage('Generating insole on server...')
    setLoadingDetails([
      'Generation request submitted.',
      'Waiting for STL_OUT to be produced...'
    ])
    setLoadingProgress(5)

    let stlOutFileIdBefore: string | null = null

    try {
      // Snapshot current output fileId to avoid “old STL_OUT” confusion.
      const beforeStatus = await backendService.getStlOutStatus(projectId)
      stlOutFileIdBefore = beforeStatus.exists ? beforeStatus.latest?.fileId ?? null : null

      const boundary_points = selectedPoints.map(p => ({
        x: p.position.x / scaleFactor,
        y: p.position.y / scaleFactor,
        z: p.position.z / scaleFactor
      }))

      const payload: InsoleGeneratePayload = {
        boundary_points,
        thickness_mm: thicknessMm,
        fade_length_mm: fadeLengthMm,
        spline_point_count: splinePointCount,
        stl_in_file_id: resolvedStlFileId,
        ...(footRotationDeg !== 0 && { foot_rotation_yaw_deg: footRotationDeg })
      }

      try {
        await backendService.generateInsole(projectId, payload)
        setLoadingDetails(prev => [
          ...prev.slice(-5),
          'Backend accepted generation request.'
        ])
      } catch (startErr) {
        const startMsg = startErr instanceof Error ? startErr.message : 'Unknown start error'
        const lower = startMsg.toLowerCase()
        const likelyGatewayTimeout =
          lower.includes('timeout') ||
          lower.includes('gateway') ||
          lower.includes('504') ||
          lower.includes('502')

        if (!likelyGatewayTimeout) {
          throw startErr
        }

        setLoadingMessage('Generation request delayed; checking server output...')
        setLoadingDetails(prev => [
          ...prev.slice(-5),
          `Start request delayed (${startMsg}). Continuing with STL_OUT polling.`
        ])
      }

      // Poll stl-out/status until a new file appears.
      // Hard timeout at 10 minutes; bail after 20 consecutive fetch failures.
      const POLL_TIMEOUT_MS = 40 * 60 * 1000
      const MAX_CONSECUTIVE_FAILURES = 20
      const startTs = Date.now()
      let pollAttempt = 0
      let consecutiveFailures = 0
      // eslint-disable-next-line no-constant-condition
      while (true) {
        pollAttempt += 1

        // User clicked Cancel
        if (cancelRequestedRef.current) {
          throw new Error('Generation canceled by user.')
        }

        // Hard timeout
        const elapsedMs = Date.now() - startTs
        if (elapsedMs > POLL_TIMEOUT_MS) {
          throw new Error(`Insole generation timed out after ${Math.floor(elapsedMs / 1000)}s. The server may still be processing — check STL_OUT later.`)
        }

        try {
          const status = await backendService.getStlOutStatus(projectId)
          consecutiveFailures = 0 // Reset on success

          // Check if backend flagged generation as failed
          if ((status as any).projectStatus === 'insole_generation_failed') {
            throw new Error('Insole generation failed on the server. Check boundary points and try again.')
          }

          const latestFileId = status.exists ? status.latest?.fileId ?? null : null

          if (latestFileId && latestFileId !== stlOutFileIdBefore) {
            const elapsedSec = Math.floor((Date.now() - startTs) / 1000)
            setLoadingMessage('Generation complete. Loading STL_OUT...')
            setLoadingDetails(prev => [
              ...prev.slice(-5),
              `STL_OUT ready after ${elapsedSec}s.`
            ])
            setLoadingProgress(95)
            break
          }

          if (pollAttempt === 1 || pollAttempt % 5 === 0) {
            const elapsedSec = Math.floor((Date.now() - startTs) / 1000)
            setLoadingMessage(`Generating insole... ${elapsedSec}s elapsed`)
            setLoadingDetails(prev => [
              ...prev.slice(-5),
              `No STL_OUT yet (${pollAttempt} checks, ${elapsedSec}s).`
            ])
            setLoadingProgress(15 + (pollAttempt % 70))
          }
        } catch (pollError) {
          consecutiveFailures += 1
          if (pollAttempt % 5 === 0) {
            const pollMsg = pollError instanceof Error ? pollError.message : 'Unknown polling error'
            setLoadingDetails(prev => [
              ...prev.slice(-5),
              `Status check retrying (${consecutiveFailures} failures): ${pollMsg}`
            ])
          }
          if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
            throw new Error(`Backend unreachable after ${consecutiveFailures} consecutive failures. Check your connection and try again.`)
          }
        }

        await new Promise(resolve => setTimeout(resolve, 1000))
      }

      const finalStatus = await backendService.getStlOutStatus(projectId)
      const finalFileId = finalStatus.exists ? finalStatus.latest?.fileId ?? null : null
      if (!finalFileId || finalFileId === stlOutFileIdBefore) {
        throw new Error('Generation did not produce a new STL_OUT yet. Please retry.')
      }

      // Open/ensure STL_OUT tab is visible.
      // (We can refine labels later in execution plan Step 3.)
      tabContext.addSTLOutView(`Project ${projectId}`, 'Unknown Patient', projectId, finalFileId)
      setShowInsoleParamsModal(false)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to generate insole'
      setLoadingError(msg)
      alert(msg)
      logError(msg, 'GirasolesView')
    } finally {
      setIsGeneratingInsole(false)
      setIsLoadingSTL(false)
      setLoadingStartedAt(null)
      setLoadingDetails([])
      setLoadingProgress(undefined)
      activeV1ProjectIdRef.current = null
      cancelRequestedRef.current = false
      setIsCancelling(false)
    }
  }

  // V2 generate: triggered by TemplateUploadDialog callback
  const handleV2GenerateFromDialog = async (
    v2AlignResult: AlignResult,
    v2AdjustTranslation: [number, number],
    v2AdjustRotation: number,
    v2AdjustScale: number,
    v2Refine?: import('../../services/types/insoleV2').RefineParams,
    v2Shell?: import('../../services/types/insoleV2').ShellParams
  ) => {
    if (!projectId || !currentStlFileId) return

    // Flush any pending adjustments to the backend before draping
    let currentAlignResult = v2AlignResult
    const [tx, ty] = v2AdjustTranslation
    if (tx !== 0 || ty !== 0 || v2AdjustRotation !== 0 || v2AdjustScale !== 1.0) {
      try {
        const adjusted = await backendService.adjustAlignment({
          session_id: v2AlignResult.session_id,
          translation: v2AdjustTranslation,
          rotation: v2AdjustRotation,
          scale: v2AdjustScale,
        })
        currentAlignResult = {
          ...v2AlignResult,
          alignment_id: adjusted.alignment_id,
          aligned_template_2d: adjusted.aligned_template_2d,
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Adjustment failed'
        logError(`V2 adjustment error: ${msg}`, 'GirasolesView')
        return
      }
    }

    setIsRunningV2Pipeline(true)
    setShowV2TemplateModal(false)
    setIsLoadingSTL(true)
    setLoadingStartedAt(Date.now())
    setLoadingMessage('V2 Pipeline: Draping template onto foot...')
    setLoadingDetails(['Alignment confirmed. Starting drape...'])
    setLoadingProgress(10)

    cancelRequestedRef.current = false
    activeV2SessionIdRef.current = currentAlignResult.session_id
    setIsCancelling(false)

    let stlOutFileIdBefore: string | null = null

    try {
      // Snapshot current output fileId
      const beforeStatus = await backendService.getStlOutStatus(projectId)
      stlOutFileIdBefore = beforeStatus.exists ? beforeStatus.latest?.fileId ?? null : null

      // Drape
      setLoadingMessage('V2 Pipeline: Draping template onto foot...')
      setLoadingProgress(20)
      try {
        await backendService.drapeTemplate({
          session_id: currentAlignResult.session_id,
          aligned_template_2d: currentAlignResult.aligned_template_2d,
          stl_file_id: currentStlFileId,
        })
      } catch (drapeErr) {
        // Backend PR #414/#415: drape rejects uncalibrated scans with a
        // structured detail. Rewrite the message so the doctor sees the
        // calibration path instead of "Request failed with status 400".
        const { isCalibrationRequiredError, formatAlignError } = await import(
          '../../services/api/insoleV2Api'
        )
        if (isCalibrationRequiredError(drapeErr)) {
          throw new Error(formatAlignError(drapeErr))
        }
        throw drapeErr
      }

      // Approve boundary
      setLoadingMessage('V2 Pipeline: Approving boundary...')
      setLoadingProgress(50)
      const approveResult = await backendService.approveBoundary({
        session_id: currentAlignResult.session_id,
      })

      // Generate
      setLoadingMessage('V2 Pipeline: Generating insole mesh...')
      setLoadingProgress(60)
      // Merge in user's "Advanced Insole Options" overrides (Tools menu).
      const advanced = loadAdvancedRefine()
      const mergedRefine = advanced
        ? { ...(v2Refine || {}), ...advanced, enabled: true }
        : v2Refine
      try {
        await backendService.generateInsoleV2({
          session_id: currentAlignResult.session_id,
          approved_boundary_id: approveResult.approved_boundary_id,
          ...(mergedRefine ? { refine: mergedRefine } : {}),
          ...(v2Shell ? { shell: v2Shell } : {}),
        })
      } catch (startErr) {
        const startMsg = startErr instanceof Error ? startErr.message : 'Unknown start error'
        const lower = startMsg.toLowerCase()
        const likelyGatewayTimeout =
          lower.includes('timeout') ||
          lower.includes('gateway') ||
          lower.includes('504') ||
          lower.includes('502')

        if (!likelyGatewayTimeout) {
          throw startErr
        }

        setLoadingMessage('V2 request delayed; checking server output...')
        setLoadingDetails(prev => [
          ...prev.slice(-5),
          `V2 start delayed (${startMsg}). Continuing with STL_OUT polling.`
        ])
      }

      // Poll stl-out/status with 5 minute hard timeout
      const startTs = Date.now()
      const maxWaitMs = 5 * 60 * 1000
      let pollAttempt = 0
      let consecutiveErrors = 0
      // eslint-disable-next-line no-constant-condition
      while (true) {
        pollAttempt += 1

        if (cancelRequestedRef.current) {
          throw new Error('V2 generation canceled by user.')
        }

        if (Date.now() - startTs > maxWaitMs) {
          throw new Error('V2 generation timed out after 5 minutes. The server may still be processing — check the STL_OUT view later.')
        }

        try {
          const status = await backendService.getStlOutStatus(projectId)
          const latestFileId = status.exists ? status.latest?.fileId ?? null : null
          consecutiveErrors = 0

          if (latestFileId && latestFileId !== stlOutFileIdBefore) {
            const elapsedSec = Math.floor((Date.now() - startTs) / 1000)
            setLoadingMessage('V2 generation complete. Loading STL_OUT...')
            setLoadingDetails(prev => [
              ...prev.slice(-5),
              `V2 STL_OUT ready after ${elapsedSec}s.`
            ])
            setLoadingProgress(95)
            break
          }

          if (pollAttempt === 1 || pollAttempt % 5 === 0) {
            const elapsedSec = Math.floor((Date.now() - startTs) / 1000)
            setLoadingMessage(`V2 generating mesh... ${elapsedSec}s elapsed`)
            setLoadingDetails(prev => [
              ...prev.slice(-5),
              `Still processing (${pollAttempt} checks, ${elapsedSec}s).`
            ])
            setLoadingProgress(60 + Math.min(30, pollAttempt))
          }
        } catch (pollError) {
          consecutiveErrors += 1
          if (consecutiveErrors >= 15) {
            throw new Error('Backend appears to be down. V2 generation may have failed. Check backend logs.')
          }
          if (pollAttempt % 5 === 0) {
            const pollMsg = pollError instanceof Error ? pollError.message : 'Unknown polling error'
            setLoadingDetails(prev => [
              ...prev.slice(-5),
              `V2 status check retrying (${consecutiveErrors} errors): ${pollMsg}`
            ])
          }
        }

        await new Promise(resolve => setTimeout(resolve, 2000))
      }

      const finalStatus = await backendService.getStlOutStatus(projectId)
      const finalFileId = finalStatus.exists ? finalStatus.latest?.fileId ?? null : null
      if (!finalFileId || finalFileId === stlOutFileIdBefore) {
        throw new Error('V2 generation did not produce a new STL_OUT yet. Please retry.')
      }

      tabContext.addSTLOutView(`Project ${projectId}`, 'Unknown Patient', projectId, finalFileId)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'V2 pipeline failed'
      setLoadingError(msg)
      logError(msg, 'GirasolesView')
    } finally {
      setIsRunningV2Pipeline(false)
      setIsLoadingSTL(false)
      setLoadingStartedAt(null)
      setLoadingDetails([])
      setLoadingProgress(undefined)
      activeV2SessionIdRef.current = null
      cancelRequestedRef.current = false
      setIsCancelling(false)
    }
  }

  // Cancel any in-flight V1 or V2 insole generation. Flips the local
  // cancel flag (so polling loops bail out) and POSTs to the matching
  // server-side cancel endpoint so backend stages stop early.
  const handleCancelGeneration = useCallback(async () => {
    if (cancelRequestedRef.current || isCancelling) return
    cancelRequestedRef.current = true
    setIsCancelling(true)
    setLoadingMessage('Cancelling generation...')
    setLoadingDetails(prev => [...prev.slice(-5), 'Cancellation requested by user.'])

    const v2Session = activeV2SessionIdRef.current
    const v1Project = activeV1ProjectIdRef.current
    try {
      if (v2Session) {
        await backendService.cancelV2Session(v2Session)
      } else if (v1Project) {
        await backendService.cancelClassicInsole(v1Project)
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Cancel request failed'
      logError(`Cancel request failed: ${msg}`, 'GirasolesView')
      setLoadingDetails(prev => [...prev.slice(-5), `Server cancel failed: ${msg}`])
    }
  }, [isCancelling])

  return (
    <div className="girasoles-view-window">
      {/* Menu Bar */}
      <div className="girasoles-menu-bar">
        <div className="menu-items">
          {/* Tools Menu */}
          <span
            className="menu-item tools-menu"
            onClick={() => setShowToolsMenu(!showToolsMenu)}
          >
            Tools
          </span>
          {showToolsMenu && (
            <div className="tools-dropdown">
              <div
                className="dropdown-item"
                onClick={() => {
                  setShowObjectManager(true)
                  setShowToolsMenu(false)
                }}
              >
                Object Manager
              </div>
              <div className="dropdown-divider"></div>
              <div
                className="dropdown-item"
                onClick={() => {
                  if (showCalibrationLandmarks) {
                    // Turn off: hide landmarks and exit edit mode
                    handleToggleCalibrationLandmarks()
                    setIsEditingCalibrationLandmarks(false)
                  } else {
                    // Turn on: show landmarks and enter edit mode
                    handleToggleCalibrationLandmarks().then(() => {
                      setIsEditingCalibrationLandmarks(true)
                    })
                  }
                  setShowToolsMenu(false)
                }}
              >
                {showCalibrationLandmarks ? '✓ Calibration Landmarks' : 'Calibration Landmarks'}
              </div>
              <div className="dropdown-divider"></div>
              <div
                className="dropdown-item"
                onClick={() => {
                  setShowTemplateVisualizer(true)
                  setShowToolsMenu(false)
                }}
              >
                Visualize Template…
              </div>
              {templateOverlay && (
                <div
                  className="dropdown-item"
                  onClick={() => {
                    setTemplateOverlay({ ...templateOverlay, visible: !templateOverlay.visible })
                    setShowToolsMenu(false)
                  }}
                >
                  {templateOverlay.visible
                    ? `✓ Hide Template Overlay (${templateOverlay.name})`
                    : `Show Template Overlay (${templateOverlay.name})`}
                </div>
              )}
              {isAuthenticated && (
                <>
                  <div className="dropdown-divider"></div>
                  <div
                    className="dropdown-item"
                    onClick={() => {
                      setShowAdvancedInsoleOptions(true)
                      setShowToolsMenu(false)
                    }}
                  >
                    Advanced Insole Options…
                  </div>
                </>
              )}
            </div>
          )}

        </div>
      </div>

      {/* Toolbar */}
      <div className="girasoles-toolbar">
        {selectedPoints.length > 0 && (
          <>
            <button
              className={`toolbar-btn ${unselectMode ? 'active' : ''}`}
              onClick={() => setUnselectMode(!unselectMode)}
              title={unselectMode ? 'Switch to Select mode' : 'Switch to Unselect mode — click near a point to remove it'}
            >
              {unselectMode ? 'Unselect' : 'Select'}
            </button>
            <button
              className="toolbar-btn clear-points-btn"
              onClick={handleClearPoints}
              title={`Clear ${selectedPoints.length} selected points`}
            >
              Clear Points ({selectedPoints.length})
            </button>
          </>
        )}
      </div>

      {/* Main Content Area */}
      <div className="girasoles-content">
        {/* Side Panel: Object Manager + Tools (anchored on the RIGHT) */}
        <div className="left-panel" style={{ width: `${leftPanelWidth}px` }}>
          <ResizableSection storageKey="gv.section.objects.h" defaultHeight={120} minHeight={60}>
            <ObjectManager
              objects={displayedSceneObjects}
              onToggleVisibility={handleDisplayedVisibilityToggle}
              onSelectObject={handleObjectSelect}
              onDeleteObject={handleObjectDelete}
              selectedObjectId={selectedObjectId}
            />
          </ResizableSection>

          <ResizableSection storageKey="gv.section.foot.h" defaultHeight={100} minHeight={50}>
            <FootMeasurementsCard
              measurements={footMeasurements}
              loading={footMeasurementsLoading}
            />
          </ResizableSection>

          {/* Calibration Landmarks Panel — visible only when editing */}
          {showCalibrationLandmarks && calibrationLandmarks.length > 0 && (
            <ResizableSection storageKey="gv.section.calib.h" defaultHeight={isEditingCalibrationLandmarks ? 160 : 90} minHeight={60}>
              <div style={{
                margin: '6px 4px 0',
                padding: '8px 10px',
                background: '#ffffff',
                border: '1px solid #d0d7de',
                borderRadius: 6,
                fontSize: 11,
                color: '#1f2328',
              }}>
                <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>🎯 Calibration Landmarks</span>
                  <button
                    onClick={() => setIsEditingCalibrationLandmarks(!isEditingCalibrationLandmarks)}
                    style={{
                      padding: '2px 6px', fontSize: 10, fontWeight: 600,
                      background: isEditingCalibrationLandmarks ? '#cf222e' : '#d0d7de',
                      color: isEditingCalibrationLandmarks ? '#fff' : '#1f2328',
                      border: 'none', borderRadius: 3, cursor: 'pointer'
                    }}
                  >
                    {isEditingCalibrationLandmarks ? 'Done' : 'Edit'}
                  </button>
                </div>
                {pickingCalibrationPoint && (
                  <div style={{
                    marginBottom: 6, padding: '4px 6px',
                    background: '#fff3cd', border: '1px solid #ffc107',
                    borderRadius: 4, fontSize: 10, color: '#856404'
                  }}>
                    🎯 Click on the 3D model to place <strong>{
                      pickingCalibrationPoint === 'heel' ? 'Heel' :
                      pickingCalibrationPoint === 'mt1' ? 'MT1' : 'MT5'
                    }</strong> point
                  </div>
                )}
                {calibrationLandmarks.map(lm => {
                  const c = lm.coordinates_3d
                  // Convert scene-space coords back to real-world mm for display
                  const sf = currentStlData?.scaleFactor ?? HARDCODED_SCALE_FACTOR
                  const invSf = sf !== 0 ? 1 / sf : 1
                  const mm = { x: c.x * invSf, y: c.y * invSf, z: c.z * invSf }
                  const label = lm.landmark_type === 'Heel' ? '� Heel' : lm.landmark_type === 'MT1' ? '🟢 MT1' : '🔴 MT5'
                  const isPickingThis = pickingCalibrationPoint === lm.id
                  return (
                    <div key={lm.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '2px 0' }}>
                      <span style={{ fontWeight: 500, flex: 1 }}>{label}</span>
                      <span style={{ fontFamily: 'monospace', fontSize: 10 }}>
                        ({mm.x.toFixed(2)}, {mm.y.toFixed(2)}, {mm.z.toFixed(2)})
                      </span>
                      {isEditingCalibrationLandmarks && (
                        <button
                          onClick={() => {
                            setPickingCalibrationPoint(isPickingThis ? null : lm.id)
                          }}
                          style={{
                            marginLeft: 4, padding: '1px 5px', fontSize: 9, fontWeight: 600,
                            background: isPickingThis ? '#cf222e' : '#d0d7de',
                            color: isPickingThis ? '#fff' : '#1f2328',
                            border: 'none', borderRadius: 3, cursor: 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                          title={isPickingThis ? 'Cancel re-pick' : 'Click to re-place this point on the 3D model'}
                        >
                          {isPickingThis ? 'Cancel' : 'Re-pick'}
                        </button>
                      )}
                    </div>
                  )
                })}
                {isEditingCalibrationLandmarks && (
                  <>
                    <div style={{ marginTop: 4, fontSize: 10, color: '#57606a' }}>
                      Drag the colored spheres to adjust, or use Re-pick to click a new position on the 3D model.
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                      <button
                        onClick={handleAcceptCalibration}
                        style={{
                          flex: 1, padding: '4px 0', fontSize: 11, fontWeight: 600,
                          background: '#2da44e', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer'
                        }}
                      >
                        Accept
                      </button>
                      <button
                        onClick={handleCancelCalibration}
                        style={{
                          flex: 1, padding: '4px 0', fontSize: 11, fontWeight: 600,
                          background: '#cf222e', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer'
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleResetCalibration}
                        style={{
                          flex: 1, padding: '4px 0', fontSize: 11, fontWeight: 600,
                          background: '#d0d7de', color: '#1f2328', border: 'none', borderRadius: 4, cursor: 'pointer'
                        }}
                      >
                        Reset
                      </button>
                    </div>
                  </>
                )}
              </div>
            </ResizableSection>
          )}

          <ResizableSection storageKey="gv.section.insole.h" defaultHeight={90} minHeight={50}>
            <InsoleCharacteristicsCard selection={insoleSelection} />
          </ResizableSection>

          <ResizableSection storageKey="gv.section.tools.h" defaultHeight={200} minHeight={120} last>
            <ToolPalette
              selectedTool={selectedTool}
              onToolSelect={handleToolSelect}
            />
          </ResizableSection>
        </div>

        {/* Width resize handle (between panel and canvas — drag right
            to widen the panel, drag left to shrink). */}
        <div
          className="panel-resize-handle"
          onMouseDown={handleResizeStart}
          title="Drag to resize panel"
        >
          <div className="resize-grip-vertical">⋮</div>
        </div>

        {/* Main Canvas Area */}
        <div className="canvas-area" ref={canvasAreaRef}>
          <Canvas3D
            objects={sceneObjects}
            selectedTool={selectedTool}
            stlData={currentStlData}
            selectedPoints={selectedPoints}
            measurePoints={measurePoints}
            measureDistance={measureDistance}
            onPointSelect={handlePointSelect}
            onSceneObjectsChange={handleSceneObjectsChange}
            selectedObjectId={selectedObjectId}
            calibrationLandmarks={calibrationLandmarks}
            showCalibrationLandmarks={showCalibrationLandmarks}
            isEditingCalibrationLandmarks={isEditingCalibrationLandmarks}
            onCalibrationLandmarkUpdate={(landmarkId, newPos) => {
              // Update local landmark state via functional updater
              _setCalibrationLandmarks(prev => {
                const updated = prev.map(l =>
                  l.id === landmarkId
                    ? { ...l, coordinates_3d: { x: newPos.x, y: newPos.y, z: newPos.z } }
                    : l
                )
                // Save updated calibration to backend in real-world mm
                if (currentStlFileId) {
                  const h = updated.find(l => l.id === 'heel')!.coordinates_3d
                  const m1 = updated.find(l => l.id === 'mt1')!.coordinates_3d
                  const m5 = updated.find(l => l.id === 'mt5')!.coordinates_3d
                  const sf = currentStlData?.scaleFactor ?? HARDCODED_SCALE_FACTOR
                  const invSf = sf !== 0 ? 1 / sf : 1
                  backendService.saveCalibration(currentStlFileId, {
                    heel: { x: h.x * invSf, y: h.y * invSf, z: h.z * invSf },
                    mt1:  { x: m1.x * invSf, y: m1.y * invSf, z: m1.z * invSf },
                    mt5:  { x: m5.x * invSf, y: m5.y * invSf, z: m5.z * invSf },
                  }).catch(err => logError(`Failed to save calibration: ${err}`, 'GirasolesView'))
                }
                return updated
              })
            }}
            pickingCalibrationPoint={pickingCalibrationPoint}
            onCalibrationPointPicked={handleCalibrationPointPicked}
            templateOverlay={templateOverlay}
            transformMode={
              templateOverlay
                ? v2GizmoMode
                : selectedTool === 'rotate'
                  ? 'rotate'
                  : selectedTool === 'scale'
                    ? 'scale'
                    : 'translate'
            }
            onTemplateTransform={(delta) => {
              // Forward gizmo deltas to the V2 align dialog via a window event.
              window.dispatchEvent(new CustomEvent('gv:v2-template-transform', { detail: delta }))
            }}
            onTemplateTransformEnd={() => {
              window.dispatchEvent(new CustomEvent('gv:v2-template-transform-end'))
            }}
          />

          {/* Bottom-left Navigator panel: gizmo-mode toggle (when V2
              template overlay is active) + canonical-view snap buttons +
              live readout of the current camera view. Use this readout
              ("View: ISO" / "View: +Y (Top)" etc. plus az/el/dist) to
              tell support exactly what you are seeing. */}
          <div
            className="gv-nav-panel"
            style={{
              position: 'absolute',
              left: 10,
              bottom: 46,
              zIndex: 1010,
              background: 'rgba(20, 20, 24, 0.85)',
              color: '#e5e7eb',
              border: '1px solid #3f3f46',
              borderRadius: 8,
              padding: '8px 10px',
              fontSize: 11,
              fontFamily: 'system-ui, sans-serif',
              boxShadow: '0 4px 12px rgba(0,0,0,0.35)',
              userSelect: 'none',
              minWidth: 168,
            }}
          >
            {templateOverlay && (
              <div style={{ marginBottom: 6 }}>
                <div style={{ opacity: 0.7, marginBottom: 4 }}>Template gizmo</div>
                <div style={{ display: 'flex', gap: 4 }}>
                  {(['translate', 'rotate', 'scale'] as const).map((m) => (
                    <button
                      key={m}
                      onClick={() => setV2GizmoMode(m)}
                      style={{
                        flex: 1,
                        padding: '4px 6px',
                        fontSize: 11,
                        background: v2GizmoMode === m ? '#2563eb' : '#27272a',
                        color: '#fff',
                        border: '1px solid #3f3f46',
                        borderRadius: 4,
                        cursor: 'pointer',
                        textTransform: 'capitalize',
                      }}
                    >
                      {m === 'translate' ? 'Move' : m === 'rotate' ? 'Rotate' : 'Scale'}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div style={{ opacity: 0.7, marginBottom: 4 }}>View</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4 }}>
              {(['+Y', '-Y', '+X', '-X', '+Z', '-Z', 'ISO'] as const).map((v) => (
                <button
                  key={v}
                  onClick={() =>
                    window.dispatchEvent(new CustomEvent('gv:snap-view', { detail: { view: v } }))
                  }
                  title={
                    v === '+Y'
                      ? 'Top (look down +Y)'
                      : v === '-Y'
                        ? 'Bottom (look up -Y)'
                        : v === '+X'
                          ? 'Right'
                          : v === '-X'
                            ? 'Left'
                            : v === '+Z'
                              ? 'Front'
                              : v === '-Z'
                                ? 'Back'
                                : 'Isometric'
                  }
                  style={{
                    padding: '4px 0',
                    fontSize: 11,
                    background: viewInfo.label.startsWith(v) ? '#2563eb' : '#27272a',
                    color: '#fff',
                    border: '1px solid #3f3f46',
                    borderRadius: 4,
                    cursor: 'pointer',
                  }}
                >
                  {v}
                </button>
              ))}
            </div>
            <div style={{ marginTop: 6, opacity: 0.85, fontVariantNumeric: 'tabular-nums' }}>
              {viewInfo.label}
              <span style={{ opacity: 0.6 }}>
                {' '}· az {viewInfo.azim.toFixed(0)}° el {viewInfo.elev.toFixed(0)}° d{' '}
                {viewInfo.dist.toFixed(1)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* STL Object Manager Modal */}
      {showObjectManager && projectId && (
        <STLObjectManager
          projectId={projectId}
          currentFileId={currentStlFileId}
          onClose={() => setShowObjectManager(false)}
          onSelectSTL={handleSTLObjectSelect}
        />
      )}
      {showObjectManager && !projectId && (
        <div className="error-modal">
          <h3>No Project ID</h3>
          <p>Cannot open STL Object Manager without a valid project ID.</p>
          <button onClick={() => setShowObjectManager(false)}>Close</button>
        </div>
      )}

      {/* Export STL Confirmation Modal */}
      {showExportModal && (
        <div className="modal-overlay" onClick={() => setShowExportModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Export STL</h3>
              <button className="modal-close" onClick={() => setShowExportModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p>This operation will cost <strong>10 tokens</strong></p>
              <p>Your current balance: <strong>{tokenBalance !== null ? `${tokenBalance} tokens` : 'Loading...'}</strong></p>
              {tokenBalance !== null && tokenBalance < 10 && (
                <p style={{color: '#d32f2f', fontWeight: 'bold', marginTop: '12px'}}>
                  ⚠️ Insufficient tokens. Please add more tokens to continue.
                </p>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowExportModal(false)}>Cancel</button>
              <button
                className="btn-primary"
                onClick={handleConfirmExport}
                disabled={tokenBalance === null || tokenBalance < 10 || isExporting}
                style={{
                  opacity: (tokenBalance === null || tokenBalance < 10 || isExporting) ? 0.5 : 1,
                  cursor: (tokenBalance === null || tokenBalance < 10 || isExporting) ? 'not-allowed' : 'pointer'
                }}
              >
                {isExporting ? 'Exporting...' : 'Confirm Export'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Insole Parameters Modal */}
      {showInsoleParamsModal && (
        <div className="modal-overlay" onClick={() => !isGeneratingInsole && setShowInsoleParamsModal(false)}>
          <div className="modal-content insole-params-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Generate Insole</h3>
              <button
                className="modal-close"
                onClick={() => setShowInsoleParamsModal(false)}
                disabled={isGeneratingInsole}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="insole-modal-field">
                <label htmlFor="insole-thickness">Insole thickness (mm):</label>
                <input
                  id="insole-thickness"
                  type="number"
                  min={0.1}
                  step={0.1}
                  value={thicknessMm}
                  onChange={(e) => setThicknessMm(Number(e.target.value))}
                  disabled={isGeneratingInsole}
                />
              </div>
              <div className="insole-modal-field">
                <label htmlFor="insole-fade">Fade length (mm):</label>
                <input
                  id="insole-fade"
                  type="number"
                  min={0.1}
                  step={0.5}
                  value={fadeLengthMm}
                  onChange={(e) => setFadeLengthMm(Number(e.target.value))}
                  disabled={isGeneratingInsole}
                />
              </div>
              <div className="insole-modal-field">
                <label htmlFor="insole-spline">Number of points per curve:</label>
                <input
                  id="insole-spline"
                  type="number"
                  min={1000}
                  step={100}
                  value={splinePointCount}
                  onChange={(e) => setSplinePointCount(Number(e.target.value))}
                  disabled={isGeneratingInsole}
                />
              </div>
              {/* Advanced Options */}
              <div className="advanced-options-section">
                <button
                  type="button"
                  className="advanced-options-toggle"
                  onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                >
                  {showAdvancedOptions ? '▾' : '▸'} Advanced Options
                </button>
                {showAdvancedOptions && (
                  <div className="advanced-options-content">
                    <div className="insole-modal-checks">
                      <label>
                        <input
                          type="checkbox"
                          checked={invertTopBottom}
                          onChange={(e) => setInvertTopBottom(e.target.checked)}
                          disabled={isGeneratingInsole}
                        />
                        Invert top/bottom
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={invertFrontBack}
                          onChange={(e) => setInvertFrontBack(e.target.checked)}
                          disabled={isGeneratingInsole}
                        />
                        Invert front/back
                      </label>
                    </div>
                    {/* Foot rotation adjustment */}
                    <div className="insole-modal-field">
                      <label htmlFor="foot-rotation">Foot rotation (yaw °):</label>
                      <div className="rotation-control">
                        <button
                          type="button"
                          className="rotation-step-btn"
                          onClick={() => setFootRotationDeg(prev => Math.max(-45, prev - 1))}
                          disabled={isGeneratingInsole || footRotationDeg <= -45}
                        >−</button>
                        <input
                          id="foot-rotation"
                          type="range"
                          min={-45}
                          max={45}
                          step={1}
                          value={footRotationDeg}
                          onChange={(e) => setFootRotationDeg(Number(e.target.value))}
                          disabled={isGeneratingInsole}
                          className="rotation-slider"
                        />
                        <button
                          type="button"
                          className="rotation-step-btn"
                          onClick={() => setFootRotationDeg(prev => Math.min(45, prev + 1))}
                          disabled={isGeneratingInsole || footRotationDeg >= 45}
                        >+</button>
                        <span className={`rotation-value ${footRotationDeg !== 0 ? 'non-zero' : ''}`}>
                          {footRotationDeg > 0 ? `+${footRotationDeg}` : footRotationDeg}°
                        </span>
                        {footRotationDeg !== 0 && (
                          <button
                            type="button"
                            className="rotation-reset-btn"
                            onClick={() => setFootRotationDeg(0)}
                            disabled={isGeneratingInsole}
                            title="Reset to 0°"
                          >↺</button>
                        )}
                      </div>
                    </div>
                    {/* 0° reference indicator */}
                    <div className="rotation-reference-hint">
                      <span className="reference-line" />
                      0° reference plane — adjust to align heel axis
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn-cancel"
                onClick={async () => {
                  if (isGeneratingInsole && projectId) {
                    try {
                      await backendService.cancelClassicInsole(projectId)
                    } catch (e) {
                      console.error('cancelClassicInsole failed', e)
                    }
                  }
                  setShowInsoleParamsModal(false)
                }}
              >
                {isGeneratingInsole ? 'Cancel Generation' : 'Cancel'}
              </button>
              <button
                className="btn-primary"
                onClick={handleConfirmGenerateInsole}
                disabled={isGeneratingInsole}
              >
                {isGeneratingInsole ? 'Generating...' : 'Generate Insole'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* V2 Pipeline Template Dialog */}
      <TemplateUploadDialog
        isOpen={showV2TemplateModal}
        onClose={() => setShowV2TemplateModal(false)}
        doctorId=""
        v2Mode={true}
        currentStlFileId={currentStlFileId || undefined}
        projectId={projectId}
        onGenerateRequested={handleV2GenerateFromDialog}
        pinToContainer={canvasAreaEl}
        onTemplateSelectionChange={setInsoleSelection}
        onAdjustPreview={(preview) => {
          if (!preview) {
            // Only clear if the visible overlay is the alignment preview;
            // never wipe a manually-shown template via the visualizer.
            setTemplateOverlay(prev =>
              prev && prev.id === 'v2-align-preview' ? null : prev,
            )
            return
          }
          setTemplateOverlay({
            id: 'v2-align-preview',
            name: preview.name || 'Aligned template',
            points: preview.points,
            visible: true,
            color: '#06b6d4',
            originCentroid: preview.originCentroid,
            zOffset: preview.zOffset,
          })
          // Auto-select the V2 cut template so the TransformControls gizmo
          // attaches and the user can immediately use Move/Rotate/Scale
          // tools without having to click the row in G-Object Manager.
          setSelectedObjectId('v2-insole-cut')
        }}
      />

      {/* V2 CAD Workbench (full editor with draggable boundary + explicit Generate) */}
      {showCADWorkbench && projectId && currentStlFileId && (
        <div className="modal-overlay" style={{ zIndex: 1000 }}>
          <div style={{ position: 'fixed', inset: 0, background: '#0b1220', overflow: 'auto' }}>
            <CADWorkbench
              projectId={projectId}
              stlFileId={currentStlFileId}
              onClose={() => setShowCADWorkbench(false)}
            />
          </div>
        </div>
      )}

      {/* Template Visualizer Dialog */}
      <TemplateVisualizerDialog
        isOpen={showTemplateVisualizer}
        onClose={() => setShowTemplateVisualizer(false)}
        overlay={templateOverlay}
        onOverlayChange={setTemplateOverlay}
      />

      {/* Upload Template Dialog (CIW command only — toolbar entry removed) */}
      <TemplateUploadDialog
        isOpen={showUploadTemplateModal}
        onClose={() => setShowUploadTemplateModal(false)}
        doctorId=""
      />

      {/* Advanced Insole Options Dialog */}
      <AdvancedOptionsDialog
        isOpen={showAdvancedInsoleOptions}
        onClose={() => setShowAdvancedInsoleOptions(false)}
      />


      {/* Loading Status Modal */}
      <LoadingStatus
        isLoading={isLoadingSTL}
        message={loadingMessage}
        details={loadingDetails}
        elapsedSeconds={loadingElapsedSec}
        progress={loadingProgress}
        downloadProgress={downloadProgress}
        error={loadingError}
        onErrorClose={() => setLoadingError(null)}
        onCancel={
          (isGeneratingInsole || _isRunningV2Pipeline) && !isCancelling
            ? handleCancelGeneration
            : undefined
        }
      />

      {/* Version Display */}
      <div className="version-display">
        v{APP_VERSION}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// FootMeasurementsCard
// Compact panel rendered under the G-Object Manager. Shows the calibrated
// foot measurements for the currently loaded STL (same data shown in STL_IN
// `MeasurementsPanel`) so the user does not have to switch tabs.
// ---------------------------------------------------------------------------
interface FootMeasurementsCardProps {
  measurements: {
    foot_length?: number
    foot_width?: number
    arch_height?: number
    arch_classification?: string
    heel_width?: number
    instep_height?: number
    units?: string
  } | null
  loading: boolean
}

const FootMeasurementsCard: React.FC<FootMeasurementsCardProps> = ({
  measurements,
  loading,
}) => {
  const fmt = (v: number | undefined) => (v == null ? '—' : v.toFixed(1))
  const u = measurements?.units || 'mm'
  const archColor = (cls?: string) => {
    switch ((cls || '').toLowerCase()) {
      case 'low': return '#ff6b6b'
      case 'normal': return '#51cf66'
      case 'high': return '#339af0'
      default: return '#666'
    }
  }
  return (
    <div
      style={{
        margin: '6px 4px 0',
        padding: '8px 10px',
        background: '#ffffff',
        border: '1px solid #d0d7de',
        borderRadius: 6,
        fontSize: 11,
        color: '#1f2328',
      }}
    >
      <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 6 }}>
        📏 Foot Measurements
      </div>
      {loading && (
        <div style={{ color: '#57606a', fontStyle: 'italic' }}>
          Loading…
        </div>
      )}
      {!loading && !measurements && (
        <div style={{ color: '#57606a', fontStyle: 'italic' }}>
          STL not calibrated.
        </div>
      )}
      {!loading && measurements && (
        <div style={{ display: 'grid', rowGap: 3 }}>
          <div>
            <span style={{ color: '#57606a' }}>Length:</span>{' '}
            <strong>{fmt(measurements.foot_length)} {u}</strong>
          </div>
          <div>
            <span style={{ color: '#57606a' }}>Width:</span>{' '}
            <strong>{fmt(measurements.foot_width)} {u}</strong>
          </div>
          <div>
            <span style={{ color: '#57606a' }}>Arch h.:</span>{' '}
            <strong>{fmt(measurements.arch_height)} {u}</strong>
            {measurements.arch_classification && (
              <span
                style={{
                  marginLeft: 4,
                  color: archColor(measurements.arch_classification),
                }}
              >
                ({measurements.arch_classification})
              </span>
            )}
          </div>
          <div>
            <span style={{ color: '#57606a' }}>Heel w.:</span>{' '}
            <strong>{fmt(measurements.heel_width)} {u}</strong>
          </div>
          <div>
            <span style={{ color: '#57606a' }}>Instep:</span>{' '}
            <strong>{fmt(measurements.instep_height)} {u}</strong>
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// InsoleCharacteristicsCard
// Mirrors FootMeasurementsCard. Surfaces the V2 dialog's currently picked
// template (size / gender / type / template name) so the user can see what
// the cyan overlay represents at a glance, even after closing the dialog.
// ---------------------------------------------------------------------------
interface InsoleCharacteristicsCardProps {
  selection: {
    templateId: string
    templateName: string
    size: string
    gender: string
    type: string
  } | null
}

const TYPE_LABELS: Record<string, string> = {
  full_length: 'Full Length',
  three_quarter: '3/4 Length',
  metatarsal: 'Metatarsal',
  heel_cup: 'Heel Cup',
}

const InsoleCharacteristicsCard: React.FC<InsoleCharacteristicsCardProps> = ({
  selection,
}) => {
  const typeLabel = selection ? (TYPE_LABELS[selection.type] || selection.type) : ''
  const genderLabel = selection
    ? selection.gender.charAt(0).toUpperCase() + selection.gender.slice(1)
    : ''
  return (
    <div
      style={{
        margin: '6px 4px 0',
        padding: '8px 10px',
        background: '#ffffff',
        border: '1px solid #d0d7de',
        borderRadius: 6,
        fontSize: 11,
        color: '#1f2328',
      }}
    >
      <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 6 }}>
        🩴 Insole Characteristics
      </div>
      {!selection && (
        <div style={{ color: '#57606a', fontStyle: 'italic' }}>
          No template selected.
        </div>
      )}
      {selection && (
        <div style={{ display: 'grid', rowGap: 3 }}>
          <div>
            <span style={{ color: '#57606a' }}>EU Size:</span>{' '}
            <strong>{selection.size || '—'}</strong>
          </div>
          <div>
            <span style={{ color: '#57606a' }}>Gender:</span>{' '}
            <strong>{genderLabel}</strong>
          </div>
          <div>
            <span style={{ color: '#57606a' }}>Type:</span>{' '}
            <strong>{typeLabel}</strong>
          </div>
          {selection.templateName && (
            <div
              style={{
                color: '#57606a',
                marginTop: 3,
                fontSize: 10,
                fontStyle: 'italic',
                wordBreak: 'break-word',
              }}
              title={selection.templateName}
            >
              {selection.templateName}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
